import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { Plus, Sparkles, BarChart3, Settings, Sun, Building2, Cog, Check, Clock, Heart, X, Mail } from 'lucide-react';

const Home = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.parallax-bg');

      parallaxElements.forEach((element, index) => {
        const speed = (index + 1) * 0.5; // Different speeds for each layer
        element.style.transform = `translateY(${scrolled * speed}px) scale(${1 + scrolled * 0.0001})`;
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Modal and form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [emailSignup, setEmailSignup] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    jobType: '',
    notes: '',
    phone: '',
    preferredContact: 'email',
    urgency: 'normal'
  });

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    } else if (formData.address.trim().length < 5) {
      errors.address = 'Please provide a complete address';
    }

    if (!formData.jobType) {
      errors.jobType = 'Please select a service type';
    }

    if (formData.phone && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, "serviceRequests"), {
        ...formData,
        timestamp: new Date(),
        userId: user ? user.uid : 'anonymous',
        status: 'pending',
        priority: formData.urgency
      });
      
      setSubmitSuccess(true);
      setFormData({ 
        name: '', 
        address: '', 
        jobType: '', 
        notes: '', 
        phone: '', 
        preferredContact: 'email', 
        urgency: 'normal' 
      });
      
      // Auto close modal after success
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting service request:', error);
      setFormErrors({ submit: 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitSuccess(false);
    setFormErrors({});
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    const trimmed = (emailSignup || '').trim().toLowerCase();
    if (!trimmed) {
      setEmailError('Please enter your email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setEmailSubmitting(true);
    try {
      await addDoc(collection(db, 'emailList'), {
        email: trimmed,
        createdAt: new Date(),
        source: 'homepage'
      });
      setEmailSuccess(true);
      setEmailSignup('');
    } catch (err) {
      console.error('Email signup error:', err);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-x-hidden">
      {/* Hero – Lawn */}
      <section className="relative min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat parallax-bg"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=2070&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/70 z-20" />
        <div className="absolute inset-0 bg-primary-900/20 z-20" />

        <div className="relative z-30 flex flex-col items-center justify-center min-h-screen text-white px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
            <p className="text-primary-300 text-sm uppercase tracking-widest font-medium">Lawn Care & Fertilization</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white drop-shadow-lg leading-tight">
              Driscoll{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-primary-500">Lawn</span>
              <br className="hidden sm:block" />
              <span className="text-white"> & Land</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-xl mx-auto">
              Professional fertilization, weed control, and lawn maintenance. Residential and commercial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-primary btn-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 min-h-[52px] px-8"
              >
                <Plus className="w-5 h-5 mr-2" />
                Request a quote
              </button>
              {user && (
                <button
                  onClick={handleLogout}
                  className="btn-outline border-white text-white hover:bg-white hover:text-secondary-900 transition-all duration-300 px-6 py-3"
                >
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative z-20 bg-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-2">What we do</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto">
              Lawn and landscape solutions for residential and commercial properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <Sparkles className="w-12 h-12" />
                ),
                title: "Fertilizing Services",
                description: "Professional fertilization programs designed to nourish your lawn and promote healthy growth throughout the year.",
                features: ["Seasonal Fertilizer Applications", "Soil Testing & Analysis", "Custom Nutrient Programs", "Organic & Synthetic Options"]
              },
              {
                icon: (
                  <BarChart3 className="w-12 h-12" />
                ),
                title: "Four Seasons Lawn Care",
                description: "Complete year-round lawn maintenance services tailored to each season's unique needs and challenges.",
                features: ["Spring Green-Up Programs", "Summer Maintenance Care", "Fall Winterization", "Winter Protection Services"]
              },
              {
                icon: (
                  <Settings className="w-12 h-12" />
                ),
                title: "Pre-Emergent Fertilizer",
                description: "Strategic pre-emergent applications to prevent weed seeds from germinating and protect your lawn's health.",
                features: ["Early Spring Applications", "Targeted Weed Prevention", "Lawn-Safe Formulations", "Timing Optimization"]
              },
              {
                icon: (
                  <Sun className="w-12 h-12" />
                ),
                title: "Weed Control Solutions",
                description: "Comprehensive weed control services using safe, effective methods to eliminate unwanted vegetation.",
                features: ["Targeted Weed Treatment", "Post-Emergent Applications", "Organic Weed Control", "Prevention Strategies"]
              },
              {
                icon: (
                  <Building2 className="w-12 h-12" />
                ),
                title: "Lawn Aeration & Soil Care",
                description: "Core aeration and soil treatments to improve drainage, root growth, and overall lawn health.",
                features: ["Core Aeration", "Soil Conditioning", "Thatch Removal", "Overseeding Support"]
              },
              {
                icon: (
                  <Cog className="w-12 h-12" />
                ),
                title: "Complete Lawn Maintenance",
                description: "Full-service lawn care including mowing, edging, trimming, and comprehensive maintenance programs.",
                features: ["Regular Mowing Services", "Edging & Trimming", "Lawn Health Assessment", "Custom Maintenance Plans"]
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="card-hover group"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  transform: 'translateY(0)',
                  opacity: 1,
                  transition: 'all 0.6s ease-out'
                }}
              >
                <div className="p-8">
                  <div className="text-primary-500 mb-6 group-hover:text-primary-600 transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">{service.title}</h3>
                  <p className="text-secondary-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-secondary-700">
                        <Check className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us – lawn strip */}
      <section className="relative z-20 bg-secondary-50 py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-2">Why us</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Why Choose Driscoll Lawn & Land?
            </h2>
            <p className="text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto">
              Professional care and commitment to excellence for your lawn
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <Sparkles className="w-8 h-8" />
                ),
                title: "Premium Quality",
                description: "Professional landscaping services with attention to detail and superior craftsmanship"
              },
              {
                icon: (
                  <Clock className="w-8 h-8" />
                ),
                title: "Reliable Service",
                description: "On-time delivery and consistent maintenance schedules you can count on"
              },
              {
                icon: (
                  <Heart className="w-8 h-8" />
                ),
                title: "Customer Focused",
                description: "Your satisfaction is our priority with personalized care and responsive service"
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="text-center p-8 transform hover:scale-105 transition-all duration-300"
                style={{
                  animationDelay: `${index * 0.2}s`,
                  transform: 'translateY(0)',
                  opacity: 1,
                  transition: 'all 0.6s ease-out'
                }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-4">{feature.title}</h3>
                <p className="text-secondary-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-20 bg-primary-700 py-16 sm:py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1598902108854-10e335adac99?auto=format&fit=crop&w=2070&q=60')" }}
        />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Ready for a healthier lawn?
          </h2>
          <p className="text-lg sm:text-xl text-primary-100 mb-8 max-w-xl mx-auto">
            Get a free quote. We’ll help you get the yard you want.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-white text-primary-700 hover:bg-primary-50 font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Get a free quote
          </button>
        </div>
      </section>

      {/* Email list signup */}
      <section className="relative z-20 bg-secondary-100 py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4">
            <Mail className="w-6 h-6" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-2">
            Stay updated with Driscoll Lawn & Land
          </h2>
          <p className="text-secondary-600 mb-6">
            Add your email to our list for tips, seasonal offers, and news.
          </p>
          {emailSuccess ? (
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-xl">
              <p className="text-primary-700 font-medium flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Thanks! You&apos;re on our list.
              </p>
            </div>
          ) : (
            <form onSubmit={handleEmailSignup} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={emailSignup}
                onChange={(e) => { setEmailSignup(e.target.value); setEmailError(''); }}
                placeholder="Enter your email"
                className="input flex-1 py-3 px-4"
                disabled={emailSubmitting}
              />
              <button
                type="submit"
                disabled={emailSubmitting}
                className="btn-primary py-3 px-6 whitespace-nowrap"
              >
                {emailSubmitting ? 'Adding…' : 'Add me'}
              </button>
            </form>
          )}
          {emailError && (
            <p className="text-red-600 text-sm mt-2">{emailError}</p>
          )}
        </div>
      </section>

      {/* Service Request Modal – mobile friendly */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={closeModal}
        >
          <div
            className="card w-full max-w-lg max-h-[92vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-xl flex flex-col animate-slide-up shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header – sticky on mobile */}
            <div className="flex-shrink-0 flex justify-between items-center p-4 sm:p-6 border-b border-secondary-100">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-secondary-900">Request a quote</h2>
                <p className="text-secondary-600 text-sm sm:text-base">Tell us about your project</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-3 -mr-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable form area */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">

              {/* Success Message */}
              {submitSuccess && (
                <div className="mb-4 sm:mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0" />
                    <p className="text-primary-700 font-medium text-sm sm:text-base">Request submitted successfully!</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {formErrors.submit && (
                <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{formErrors.submit}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" id="service-request-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`input ${formErrors.name ? 'input-error' : ''}`}
                      placeholder="Enter your full name"
                      required
                      disabled={isSubmitting}
                    />
                    {formErrors.name && <p className="form-error">{formErrors.name}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`input ${formErrors.phone ? 'input-error' : ''}`}
                      placeholder="(555) 123-4567"
                      disabled={isSubmitting}
                    />
                    {formErrors.phone && <p className="form-error">{formErrors.phone}</p>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address" className="form-label">
                    Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`input ${formErrors.address ? 'input-error' : ''}`}
                    placeholder="Enter the service location"
                    required
                    disabled={isSubmitting}
                  />
                  {formErrors.address && <p className="form-error">{formErrors.address}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="jobType" className="form-label">
                      Service Type *
                    </label>
                    <select
                      id="jobType"
                      name="jobType"
                      value={formData.jobType}
                      onChange={handleChange}
                      className={`input ${formErrors.jobType ? 'input-error' : ''}`}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select a service type</option>
                      <option value="lawnCare">Lawn Mowing & Care</option>
                      <option value="landscaping">Landscaping Design</option>
                      <option value="hardscaping">Hardscaping & Patios</option>
                      <option value="treeService">Tree Service</option>
                      <option value="irrigation">Irrigation Systems</option>
                      <option value="handyman">Handyman Services</option>
                      <option value="cleaning">Property Cleaning</option>
                      <option value="other">Other Services</option>
                    </select>
                    {formErrors.jobType && <p className="form-error">{formErrors.jobType}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="urgency" className="form-label">
                      Urgency Level
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="input"
                      disabled={isSubmitting}
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="emergency">Emergency</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="preferredContact" className="form-label">
                    Preferred Contact Method
                  </label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <label className="flex items-center min-h-[44px] px-3 py-2 rounded-lg hover:bg-secondary-50 cursor-pointer touch-manipulation">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === 'email'}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4"
                        disabled={isSubmitting}
                      />
                      <span>Email</span>
                    </label>
                    <label className="flex items-center min-h-[44px] px-3 py-2 rounded-lg hover:bg-secondary-50 cursor-pointer touch-manipulation">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === 'phone'}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4"
                        disabled={isSubmitting}
                      />
                      <span>Phone</span>
                    </label>
                    <label className="flex items-center min-h-[44px] px-3 py-2 rounded-lg hover:bg-secondary-50 cursor-pointer touch-manipulation">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="text"
                        checked={formData.preferredContact === 'text'}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4"
                        disabled={isSubmitting}
                      />
                      <span>Text</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Additional Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    className="input min-h-[100px]"
                    placeholder="Describe your project, special requirements, or any questions..."
                    disabled={isSubmitting}
                  />
                  <p className="form-help">
                    Include details
                  </p>
                </div>
              </form>
            </div>

            {/* Sticky submit on mobile */}
            <div className="flex-shrink-0 p-4 sm:p-6 pt-0 border-t border-secondary-100 bg-white pb-safe">
              <button
                type="submit"
                form="service-request-form"
                disabled={isSubmitting || submitSuccess}
                className="btn-primary w-full min-h-[52px] text-base touch-manipulation"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Submitting...
                  </>
                ) : submitSuccess ? (
                  'Request Submitted!'
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
